/**
 * V2ListStyleSections - Collapsible style sections for V2 options list
 *
 * Adapted from V1 ListStyleSections.tsx with two key changes:
 * 1. Padding translation: V2 uses EdgeInsets (top/end/bottom/start)
 *    instead of V1's paddingTop/Right/Bottom/Left
 * 2. Item propagation: V2 items at 'content.options.items' instead of 'list.items'
 *
 * Uses MUI Accordion directly (not SAccordion) to allow nesting
 * inside parent SAccordion.
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PaddingElement, PaddingValues } from '../../../../factories/elements';
import { StyleFieldRow } from '../../../shared/styleFields/StyleFieldRow';
import type { ListStyleSectionConfig } from '../../../../types/listSection.types';
import { FIELD_STYLE_GRID_GAP } from '../../../../config/ui';
import { EXPANDED_SECTION_BGCOLOR } from '../../../../config/editorFieldStyles';
import { getStyleInputTestId, getStyleCheckboxTestId } from '../../../../utils/testIdUtils';
import { getValueByPath } from '../../../../utils/v2FieldOperations';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

/**
 * Props for V2ListStyleSections
 */
export interface V2ListStyleSectionsProps {
  /** Style section configurations (from createV2OptionsListStyleSections) */
  sections: ListStyleSectionConfig[];

  /** Current itemLayout or kind (for visibility conditions) */
  currentItemType: string;

  /** Additional context for visibility conditions (columnLayout, selectionMode) */
  visibilityContext?: { columnLayout?: string; selectionMode?: string };

  /** V2 StepNode for reading values */
  step: FlowStepNode;

  /** Callback when a value changes */
  onChange: (path: string, value: unknown) => void;

  /** Whether editing is disabled */
  disabled?: boolean;

  /**
   * Base path to items array for style propagation.
   * When a style change targets items.0.*, it's applied to ALL items at this path.
   * Defaults to 'content.options.items' (OptionsList).
   * Set to 'content.carousel.items' for Carousel, 'content.stepper.items' for Stepper.
   */
  itemsBasePath?: string;
}

/**
 * Read V2 EdgeInsets and map to PaddingValues
 *
 * V2 insets use: { top, end, bottom, start }
 * PaddingElement expects: { paddingTop, paddingRight, paddingBottom, paddingLeft }
 */
function getV2PaddingValues(step: FlowStepNode, insetsPath: string): PaddingValues {
  const insets = getValueByPath<Record<string, number>>(step, insetsPath);
  return {
    paddingTop: typeof insets?.top === 'number' ? insets.top : 0,
    paddingRight: typeof insets?.end === 'number' ? insets.end : 0,
    paddingBottom: typeof insets?.bottom === 'number' ? insets.bottom : 0,
    paddingLeft: typeof insets?.start === 'number' ? insets.start : 0,
  };
}

/**
 * Translate PaddingElement output paths to V2 EdgeInsets paths
 *
 * PaddingElement emits: basePath.paddingTop → we need: basePath.top
 * PaddingElement emits: basePath.paddingRight → we need: basePath.end
 * PaddingElement emits: basePath.paddingBottom → we need: basePath.bottom
 * PaddingElement emits: basePath.paddingLeft → we need: basePath.start
 */
function translatePaddingPath(path: string): string {
  return path
    .replace('.paddingTop', '.top')
    .replace('.paddingRight', '.end')
    .replace('.paddingBottom', '.bottom')
    .replace('.paddingLeft', '.start');
}

/**
 * V2ListStyleSections - Renders collapsible style accordions for V2 options list
 *
 * Uses MUI Accordion directly to avoid SAccordion nesting restriction.
 */
export const V2ListStyleSections: React.FC<V2ListStyleSectionsProps> = ({
  sections,
  currentItemType,
  visibilityContext,
  step,
  onChange,
  disabled = false,
  itemsBasePath = 'content.options.items',
}) => {
  const theme = useTheme();

  // Filter sections by visibility condition
  const visibleSections = useMemo(() => {
    return sections.filter((section) => {
      if (!section.visibilityCondition) return true;
      return section.visibilityCondition(currentItemType, visibilityContext);
    });
  }, [sections, currentItemType, visibilityContext]);

  /**
   * When updating item-specific styles (title, subtitle, image, checkbox),
   * propagate the change to ALL items in the list, not just item 0.
   *
   * Detects paths like 'content.options.items.0.title.appearance.fontSize'
   * and applies to all items: items.0, items.1, items.2, etc.
   */
  const handleItemStyleChange = useCallback(
    (path: string, value: unknown) => {
      if (path.includes('.items.0.')) {
        const items = getValueByPath<unknown[]>(step, itemsBasePath);
        if (items && items.length > 0) {
          items.forEach((_, index) => {
            const itemPath = path.replace('.items.0.', `.items.${index}.`);
            onChange(itemPath, value);
          });
          return;
        }
      }
      onChange(path, value);
    },
    [step, onChange, itemsBasePath]
  );

  /**
   * Handle padding changes with V2 path translation
   * Translates PaddingElement output (paddingTop/Right/Bottom/Left)
   * to V2 EdgeInsets format (top/end/bottom/start)
   */
  const handlePaddingChange = useCallback(
    (path: string, value: number) => {
      const translatedPath = translatePaddingPath(path);
      handleItemStyleChange(translatedPath, value);
    },
    [handleItemStyleChange]
  );

  if (visibleSections.length === 0) {
    return null;
  }

  // Cast step to Record for StyleFieldRow compatibility
  const stepAsRecord = step as unknown as Record<string, unknown>;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
      data-testid="list-style-sections"
    >
      {visibleSections.map((section) => (
        <Accordion
          key={section.id}
          defaultExpanded={section.defaultExpanded}
          disableGutters
          elevation={0}
          data-testid={`style-section-${section.id}`}
          sx={{
            backgroundColor: 'transparent',
            '&:before': { display: 'none' },
            '& .MuiAccordionSummary-root': {
              minHeight: 32,
              px: 1,
              py: 0,
              backgroundColor: theme.palette.action.hover,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: theme.palette.action.selected,
              },
            },
            '& .MuiAccordionSummary-content': {
              margin: '4px 0',
            },
            '& .MuiAccordionDetails-root': {
              px: 2,
              pt: 1,
              pb: 1,
              bgcolor: EXPANDED_SECTION_BGCOLOR,
              borderRadius: 1,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            aria-controls={`${section.id}-content`}
            id={`${section.id}-header`}
          >
            <Typography variant="caption" fontWeight={500}>
              {section.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Render fields in 2-column grid if they exist */}
            {section.fields && section.fields.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: FIELD_STYLE_GRID_GAP,
                  mb: (section.customComponent || section.paddingConfig) ? 1.5 : 0,
                }}
                data-testid={`style-section-${section.id}-fields`}
              >
                {section.fields.map((fieldConfig) => (
                  <StyleFieldRow
                    key={fieldConfig.path}
                    config={fieldConfig}
                    value={getValueByPath(stepAsRecord, fieldConfig.path)}
                    onChange={(value) => handleItemStyleChange(fieldConfig.path, value)}
                    disabled={disabled}
                    inputTestId={getStyleInputTestId(section.id, fieldConfig.label)}
                    checkboxTestId={fieldConfig.isNullable ? getStyleCheckboxTestId(section.id, fieldConfig.label) : undefined}
                  />
                ))}
              </Box>
            )}

            {/* Custom component (e.g., GradientEditor) — renders after fields if both present */}
            {section.customComponent && (
              <Box sx={{ mb: section.paddingConfig ? 1.5 : 0 }}>
                <section.customComponent
                  values={stepAsRecord}
                  onChange={handleItemStyleChange}
                  disabled={disabled}
                />
              </Box>
            )}

            {/* Render padding element if configured (after fields and custom component) */}
            {section.paddingConfig && (
              <PaddingElement
                basePath={section.paddingConfig.basePath}
                values={getV2PaddingValues(step, section.paddingConfig.basePath)}
                onChange={handlePaddingChange}
                label={section.paddingConfig.label}
                disabled={disabled}
                defaultExpanded={true}
                // Stable per-padding clip target for help-article screenshots: the label +
                // cross is addressable by `padding-<basePath>` (basePath is unique per screen).
                // The directional inputs keep their own `${basePath}.paddingTop` ids.
                data-testid={`padding-${section.paddingConfig.basePath}`}
              />
            )}

            {/* Placeholder for empty sections (no fields, no custom component, and no padding) */}
            {(!section.fields || section.fields.length === 0) && !section.customComponent && !section.paddingConfig && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontStyle: 'italic' }}
                data-testid={`style-section-${section.id}-placeholder`}
              >
                Style fields coming soon
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default V2ListStyleSections;
